import { Badge, Button, Card, Group, Progress, Select, SimpleGrid, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconClock, IconConfetti, IconSchool } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { DONE, STATUS, dueLabel, fmtDate, score } from '../../lib/format';
import { useMe } from '../../lib/me';
import type { AssignmentStatus, MyCourse } from '../../lib/types';
import { PageHeader } from '../../ui/PageHeader';
import { QueryState } from '../../ui/QueryState';
import { StatusBadge } from '../../ui/StatusBadge';

function actionLabel(a: MyCourse) {
  if (a.hasOpenAttempt) return 'Продолжить тест';
  switch (a.status) {
    case 'NOT_STARTED': return 'Начать';
    case 'COMPLETED': case 'COMPLETED_LATE': case 'CANCELLED': case 'BLOCKED': return 'Открыть';
    case 'RETAKE': return 'Повторить и пересдать';
    default: return 'Продолжить';
  }
}

export function CourseCard({ a }: { a: MyCourse }) {
  const done = DONE.includes(a.status);
  const due = dueLabel(a.dueAt, done || a.status === 'CANCELLED');
  const pct = a.lessonsTotal ? Math.round((a.lessonsDone / a.lessonsTotal) * 100) : 0;
  return (
    <Card withBorder radius="lg" padding="lg" h="100%" opacity={a.status === 'CANCELLED' ? 0.6 : 1}>
      <Stack gap="sm" h="100%">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Badge variant="dot" color="gray">{a.category || 'Курс'}</Badge>
          <StatusBadge status={a.status} />
        </Group>
        <div>
          <Title order={4} mb={4}>{a.title}</Title>
          <Text size="sm" c="dimmed" lineClamp={3}>{a.description}</Text>
        </div>
        <Group gap="lg" mt="auto">
          <Group gap={4}><IconClock size={15} color="gray" /><Text size="xs" c="dimmed">~{a.durationMin} мин</Text></Group>
          <Text size="xs" c={due.color} fw={due.color === 'dimmed' ? 400 : 600}>{due.text}</Text>
        </Group>
        {done ? (
          <Text size="sm">Завершён {fmtDate(a.completedAt)} · <b>{score(a.passedCorrect, a.questionCount)}</b></Text>
        ) : a.status === 'CANCELLED' ? (
          <Text size="sm" c="dimmed">Назначение отменено {fmtDate(a.cancelledAt)}</Text>
        ) : (
          <div>
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed">Уроки: {a.lessonsDone} из {a.lessonsTotal}</Text>
              {a.attemptsUsed > 0 && <Text size="xs" c="dimmed">Попыток: {a.attemptsUsed} из {a.attemptsLimit}</Text>}
            </Group>
            <Progress value={pct} size="sm" radius="xl" color={a.status === 'RETAKE' ? 'orange' : 'blue'} />
          </div>
        )}
        <Button component={Link} to={`/learn/${a.id}`} variant={done || a.status === 'CANCELLED' ? 'default' : 'filled'} color={a.status === 'RETAKE' ? 'orange' : undefined} fullWidth>
          {actionLabel(a)}
        </Button>
      </Stack>
    </Card>
  );
}

const ACTIVE: AssignmentStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'RETAKE', 'OVERDUE', 'BLOCKED'];

export function MyCoursesPage() {
  const me = useMe();
  const [filter, setFilter] = useState<string>('active');
  const q = useQuery({ queryKey: ['my-courses'], queryFn: () => api.get<MyCourse[]>('/me/courses') });

  return (
    <>
      <PageHeader title={`Здравствуйте, ${me.fullName.split(' ')[0]}`} subtitle="Назначенные вам курсы по кибербезопасности: 2-3 коротких урока и итоговый тест." />
      <QueryState loading={q.isLoading} error={q.error}>
        {() => {
          const all = q.data!;
          const list = all.filter((a) => (filter === 'all' ? true : filter === 'active' ? ACTIVE.includes(a.status) : a.status === filter));
          const present = [...new Set(all.map((a) => a.status))];
          return (
            <Stack>
              <Select
                w={260}
                value={filter}
                onChange={(v) => setFilter(v ?? 'active')}
                allowDeselect={false}
                aria-label="Фильтр по статусу"
                data={[
                  { value: 'active', label: `Активные (${all.filter((a) => ACTIVE.includes(a.status)).length})` },
                  { value: 'all', label: `Все (${all.length})` },
                  ...present.map((s) => ({ value: s, label: `${STATUS[s].label} (${all.filter((a) => a.status === s).length})` })),
                ]}
              />
              {list.length === 0 ? (
                <Card withBorder radius="lg" p="xl">
                  <Stack align="center" gap="xs" ta="center">
                    <ThemeIcon size={52} radius="xl" variant="light" color={filter === 'active' ? 'teal' : 'gray'}>{filter === 'active' ? <IconConfetti /> : <IconSchool />}</ThemeIcon>
                    <Text fw={500}>{filter === 'active' ? 'Активных курсов нет' : 'Здесь пока пусто'}</Text>
                  </Stack>
                </Card>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>{list.map((a) => <CourseCard key={a.id} a={a} />)}</SimpleGrid>
              )}
            </Stack>
          );
        }}
      </QueryState>
    </>
  );
}
